import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import "../PurchasingUi.css";
import "./InventoryFilterPopup.css";

const dialogStack = [];
const focusSelector = 'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]';
const copy = {
  en: { all: "All", search: "Search options", apply: "Apply", cancel: "Cancel", close: "Close", clear: "Clear selection", selectVisible: "Select visible", empty: "No matching options", hint: "Choose one or more. No selection includes all options.", selected: "selected", filter: "Filter" },
  es: { all: "Todos", search: "Buscar opciones", apply: "Aplicar", cancel: "Cancelar", close: "Cerrar", clear: "Limpiar selección", selectVisible: "Seleccionar visibles", empty: "No hay opciones coincidentes", hint: "Marca una o varias opciones. Sin selección se incluyen todas.", selected: "seleccionados", filter: "Filtrar" },
};

// Confirmation dialogs are mounted in a separate portal above this workspace.
// Suspend this trap while they are open so focus and Escape belong to them.
function isActiveDialog(card) {
  return dialogStack[dialogStack.length - 1] === card && !document.querySelector(".pu-document-confirm [role='dialog']");
}

export function InventoryWorkspaceDialog({ title, children, onClose, actions }) {
  const { themeId, languageId } = usePmsTheme();
  const words = copy[languageId] || copy.en;
  const cardRef = useRef(null);
  const closeRef = useRef(onClose);
  const titleId = useId();
  closeRef.current = onClose;

  useEffect(() => {
    const previousFocus = document.activeElement;
    const card = cardRef.current;
    dialogStack.push(card);
    const focusables = () => [...card.querySelectorAll(focusSelector)].filter((element) => !element.closest("[hidden]"));
    const focusFirst = () => (card.querySelector("[data-inventory-autofocus]") || focusables()[0] || card).focus();
    focusFirst();
    const keepFocus = (event) => {
      if (isActiveDialog(card) && !card.contains(event.target)) focusFirst();
    };
    const onKey = (event) => {
      if (!isActiveDialog(card) || event.defaultPrevented) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeRef.current?.();
        return;
      }
      if (event.key !== "Tab") return;
      const elements = focusables();
      const first = elements[0] || card;
      const last = elements[elements.length - 1] || card;
      if (!card.contains(document.activeElement) || document.activeElement === card) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("focusin", keepFocus);
    document.addEventListener("keydown", onKey);
    return () => {
      dialogStack.splice(dialogStack.indexOf(card), 1);
      document.removeEventListener("focusin", keepFocus);
      document.removeEventListener("keydown", onKey);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  return createPortal(
    <div className="pilcore-purchasing-overlay inventory-workspace-overlay" data-theme={themeId}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && isActiveDialog(cardRef.current)) onClose?.();
      }}>
      <section ref={cardRef} className="inventory-workspace-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <header className="inventory-workspace-dialog__header">
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="inventory-workspace-dialog__close" onClick={onClose} aria-label={words.close}>×</button>
        </header>
        <div className="inventory-workspace-dialog__body">{children}</div>
        {actions && <footer className="inventory-workspace-dialog__actions">{actions}</footer>}
      </section>
    </div>, document.body
  );
}

export default function InventoryFilterPopup({ label, options, value, onChange, disabled = false, languageId = "en" }) {
  const words = copy[languageId] || copy.en;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState([]);
  const [query, setQuery] = useState("");
  const searchId = useId();
  const hintId = useId();
  const normalizedOptions = useMemo(() => (options || []).map((option) => ({ ...option, value: String(option.value) })), [options]);
  const selected = (value || []).map(String);
  const visible = useMemo(() => {
    const normalize = (text) => String(text).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase();
    const search = normalize(query.trim());
    return normalizedOptions.filter((option) => normalize(option.label).includes(search));
  }, [normalizedOptions, query]);
  const close = () => setOpen(false);
  const toggle = (optionValue) => setDraft((current) => current.includes(optionValue) ? current.filter((entry) => entry !== optionValue) : [...current, optionValue]);
  const apply = () => {
    const available = new Set(normalizedOptions.map((option) => option.value));
    onChange(draft.filter((entry) => available.has(entry)));
    close();
  };

  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);

  return <>
    <button type="button" className={`inventory-filter-trigger${selected.length ? " inventory-filter-trigger--active" : ""}`}
      aria-haspopup="dialog" aria-expanded={open} aria-label={`${words.filter}: ${label} · ${selected.length || words.all}`} disabled={disabled}
      onClick={() => { setDraft(selected); setQuery(""); setOpen(true); }}>
      <span>{label}</span><span className="inventory-filter-trigger__count">{selected.length || words.all}</span><span aria-hidden="true">⌄</span>
    </button>
    {open && <InventoryWorkspaceDialog title={label} onClose={close} actions={<>
      <button type="button" onClick={close}>{words.cancel}</button>
      <button type="button" className="inventory-workspace-dialog__primary" onClick={apply}>{words.apply}</button>
    </>}>
      <div className="inventory-filter-popup">
        <p id={hintId} className="inventory-filter-popup__hint">{words.hint}</p>
        <label className="inventory-filter-popup__searchLabel" htmlFor={searchId}>{words.search}</label>
        <input id={searchId} data-inventory-autofocus type="search" autoComplete="off" value={query} placeholder={words.search}
          onChange={(event) => setQuery(event.target.value)} />
        <div className="inventory-filter-popup__tools">
          <button type="button" disabled={!visible.length} onClick={() => setDraft((current) => [...new Set([...current, ...visible.map((option) => option.value)])])}>{words.selectVisible}</button>
          <button type="button" disabled={!draft.length} onClick={() => setDraft([])}>{words.clear}</button>
        </div>
        <div className="inventory-filter-popup__options" role="group" aria-label={label} aria-describedby={hintId}>
          {visible.map((option) => <label key={option.value} className={`inventory-filter-popup__option${draft.includes(option.value) ? " inventory-filter-popup__option--selected" : ""}`}>
            <input type="checkbox" checked={draft.includes(option.value)} onChange={() => toggle(option.value)} /><span>{option.label}</span>
          </label>)}
          {!visible.length && <p className="inventory-filter-popup__empty">{words.empty}</p>}
        </div>
        <p className="inventory-filter-popup__selection" role="status">{draft.length ? `${draft.length} ${words.selected}` : words.all}</p>
      </div>
    </InventoryWorkspaceDialog>}
  </>;
}
